import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useExercisePractice, Message } from './useExercisePractice';
import BotMessage from '../../components/v2/bot-message';
import SuccessBanner from '../../components/v2/success-banner';
import { LessonActionBtn } from '../../components/v2/action-buttons';
import ChatInputTool from '../../components/v2/chat-input-tool';
import UserMessage from '../../components/v2/user-message';
import { exerciseIcon, doubleVIvon, orangeVIcon, arrowFullscreen, quizClueIcon, Calculator, arrowLeft, crossIcon, calculatorIcon, Loader } from '../../assets/images';
import { IconButton } from '@mui/material';
import QRCode from 'react-qr-code';
import ReactCrop, { Crop, PixelCrop, convertToPixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import ZoomChartModal from '../../components/zoomable-image';
import { useUserStore } from '../../utils/userStore';
import { ROUTERS_PATH } from '../../routers/routers';
import MathExpressionNew from '../../components/common/mathjax';
import { hebrewLetters } from '../../configs/config';
import { showInfoToast } from '../../components/Toaster';
import { useLayoutContext } from '../../layouts/general-layout';
import Button from '../../components/button';
import ChatHeader from '../../components/chat-header';
import SolutionAnalysisModal from '../../components/exercise-review-content/SolutionAnalysisModal';
import { SearchExerciseService } from '../../services/search-exercise/search-exercise.service';
import './exercise-practice.scss';
import '../capture-exercise/capture-exercise.scss';

const ExercisePractice: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { openModesModal } = useLayoutContext();
  const {
    messages,
    isTyping,
    addUserMessage,
    onMessageComplete,
    completedMessageIds,
    fromDisplayExercise,
    exerciseQuestions,
    selectedQuestionId,
    selectedQuestion,
    selectedQuestionLetter,
    loadQuestionsList,
    selectQuestionFromBubble,
    loadQuestionSteps,
    displayStepInline,
    exerciseTitles,
    exerciseHistory,
    loadingTitles,
    exercise_list,
    isUploading,
    capturedPages,
    removePage,
    startCapture,
    isMobileActive,
    qrSessionId,
    startManualCapture,
    isPolling,
    fileToCrop,
    setFileToCrop,
    handleCroppedFile,
    solutionAnalysisData,
    isAnalysisModalOpen,
    setIsAnalysisModalOpen
  } = useExercisePractice();

  const { userInfo, user_attributes } = useUserStore();
  const [zoomImageSrc, setZoomImageSrc] = React.useState<string | null>(null);

  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
  const [showTooltip, setShowTooltip] = React.useState(false);

  useEffect(() => {
    if (location.state?.selectedExercise) {
      const hasSeenTooltip = sessionStorage.getItem("seen_calculator_tooltip");
      if (!hasSeenTooltip) {
        const timer = setTimeout(() => {
          setShowTooltip(true);
          sessionStorage.setItem("seen_calculator_tooltip", "true");
          
          setTimeout(() => {
            setShowTooltip(false);
          }, 2000);
        }, 800);
        
        return () => clearTimeout(timer);
      }
    }
  }, [location.state?.selectedExercise]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const queryExerciseId = searchParams.get('exerciseId');
    if (queryExerciseId && !location.state?.selectedExercise) {
      const fetchExerciseFromParam = async () => {
        try {
          const service = new SearchExerciseService();
          const exerciseData = await service.getExercise(parseInt(queryExerciseId, 10));
          if (exerciseData) {
            navigate(location.pathname + location.search, {
              replace: true,
              state: {
                ...location.state,
                fromDisplayExercise: true,
                selectedExercise: exerciseData,
              },
            });
          }
        } catch (err) {
          console.error("Failed to load exercise from URL param:", err);
        }
      };
      fetchExerciseFromParam();
    }
  }, [location.search, location.state, navigate, location.pathname]);

  const exerciseContent = location.state?.selectedExercise?.exercise_content;
  const capturedImageMsg = messages.find(m => m.type === 'captured_image' && m.id !== 'captured-solution-images');
  const capturedImageUrl = capturedImageMsg?.imageUrl;

  const [crop, setCrop] = React.useState<Crop>();
  const [completedCrop, setCompletedCrop] = React.useState<PixelCrop>();
  const [cropImageUrl, setCropImageUrl] = React.useState<string | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (fileToCrop) {
      const url = URL.createObjectURL(fileToCrop);
      setCropImageUrl(url);
      
      const defaultCrop: Crop = {
        unit: "%",
        x: 10,
        y: 10,
        width: 80,
        height: 80
      };
      setCrop(defaultCrop);
      
      return () => {
        URL.revokeObjectURL(url);
        setCropImageUrl(null);
        setCrop(undefined);
        setCompletedCrop(undefined);
      };
    }
  }, [fileToCrop]);

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    const { width, height } = e.currentTarget;
    const persistentCrop: Crop = {
      unit: "%",
      x: 10,
      y: 10,
      width: 80,
      height: 80
    };
    setCrop(persistentCrop);
    setCompletedCrop(convertToPixelCrop(persistentCrop, width, height));
  }

  const handleConfirmCrop = async () => {
    const image = imgRef.current;
    if (!image || !completedCrop) {
      return;
    }

    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d', { alpha: false });
      if (!ctx) return;

      const { naturalWidth, naturalHeight, width: elWidth, height: elHeight } = image;
      const crop = completedCrop;

      const naturalAspect = naturalWidth / naturalHeight;
      const elAspect = elWidth / elHeight;

      let renderedWidth, renderedHeight, xOffset, yOffset;

      if (naturalAspect > elAspect) {
        renderedWidth = elWidth;
        renderedHeight = elWidth / naturalAspect;
        xOffset = 0;
        yOffset = (elHeight - renderedHeight) / 2;
      } else {
        renderedHeight = elHeight;
        renderedWidth = elHeight * naturalAspect;
        yOffset = 0;
        xOffset = (elWidth - renderedWidth) / 2;
      }

      const finalScaleX = naturalWidth / renderedWidth;
      const finalScaleY = naturalHeight / renderedHeight;

      let sourceX = (crop.x - xOffset) * finalScaleX;
      let sourceY = (crop.y - yOffset) * finalScaleY;
      let sourceWidth = crop.width * finalScaleX;
      let sourceHeight = crop.height * finalScaleY;

      if (sourceX < 0) {
        sourceWidth += sourceX;
        sourceX = 0;
      }
      if (sourceY < 0) {
        sourceHeight += sourceY;
        sourceY = 0;
      }
      if (sourceX + sourceWidth > naturalWidth) {
        sourceWidth = naturalWidth - sourceX;
      }
      if (sourceY + sourceHeight > naturalHeight) {
        sourceHeight = naturalHeight - sourceY;
      }

      if (sourceWidth <= 0 || sourceHeight <= 0) return;

      const MAX_DIMENSION = 1920;
      let finalWidth = sourceWidth;
      let finalHeight = sourceHeight;

      if (sourceWidth > MAX_DIMENSION || sourceHeight > MAX_DIMENSION) {
        const scale = Math.min(MAX_DIMENSION / sourceWidth, MAX_DIMENSION / sourceHeight);
        finalWidth = Math.round(sourceWidth * scale);
        finalHeight = Math.round(sourceHeight * scale);
      }

      canvas.width = finalWidth;
      canvas.height = finalHeight;

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      
      if ('filter' in ctx) {
        (ctx as any).filter = 'contrast(1.15) brightness(1.02)';
      }

      ctx.drawImage(
        image,
        sourceX, sourceY, sourceWidth, sourceHeight,
        0, 0, finalWidth, finalHeight
      );

      canvas.toBlob(
        (b) => {
          if (b) {
            handleCroppedFile(b);
          }
        },
        'image/jpeg',
        0.85
      );
    } catch (err) {
      console.error("Failed to crop image:", err);
    }
  };

  const handleBotMessageNavigate = (route: string) => {
    if (route === "/search-exercise" && location.state?.returnToExerciseList) {
      navigate(ROUTERS_PATH.EXERCISE_PRACTICE, {
        state: {
          ...location.state,
          showExerciseListDirectly: true,
          fromDisplayExercise: false,
          returnToExerciseList: false,
          selectedExercise: undefined
        }
      });
    } else {
      navigate(route);
    }
  };

  const handleChartClick = (imgHtml: string) => {
    const match = imgHtml.match(/src=["']([^"']+)["']/);
    if (match) setZoomImageSrc(match[1]);
  };

  const chatContainerRef = useRef<HTMLDivElement>(null);
  const scrollContentRef = useRef<HTMLDivElement>(null);
  const pageRef = useRef<HTMLDivElement>(null);

  const userInitials = userInfo?.first_name?.charAt(0) || 'U';

  useEffect(() => {
    const container = chatContainerRef.current;
    const scrollContent = scrollContentRef.current;

    if (container && scrollContent) {
      const observer = new ResizeObserver(() => {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth'
        });
      });

      observer.observe(scrollContent);
      return () => observer.disconnect();
    }
  }, []);

  useEffect(() => {
    if (!fileToCrop && chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [fileToCrop]);

  // Auto-complete questions list, steps list, chart messages, and options messages with no text
  useEffect(() => {
    messages.forEach(msg => {
      if (
        ((msg.type === 'questions_list' || msg.type === 'steps_list' || msg.type === 'chart' || msg.type === 'captured_image' || msg.type === 'success_banner') ||
          (msg.type === 'options' && !msg.text)) &&
        !completedMessageIds.has(msg.id)
      ) {
        onMessageComplete(msg.id);
      }
    });
  }, [messages, completedMessageIds, onMessageComplete]);

  const renderMessage = (msg: Message) => {
    if (msg.sender === 'user') {
      if (msg.type === 'captured_image') {
        const urls = msg.imageUrls && msg.imageUrls.length > 0 ? msg.imageUrls : (msg.imageUrl ? [msg.imageUrl] : []);
        const isLiveCapture = msg.id === 'captured-solution-images';
        
        return (
          <div key={msg.id} className="captured-solution-gallery-container fade-in">
            {urls.map((url, index) => (
              <div key={index} className="chat-image-block captured-solution-image-block">
                <div className="captured-solution-image-wrapper">
                  <div className="captured-solution-image-click-area" onClick={() => setZoomImageSrc(url)}>
                    <img
                      src={arrowFullscreen}
                      alt="Enlarge"
                      className="fullscreen-icon"
                      title={t("enlarge")}
                    />
                    <img
                      src={url}
                      alt={`${t('capturedSolution')} - ${index + 1}`}
                      className="captured-solution-image"
                    />
                    <div className="page-number-badge">{index + 1}</div>
                  </div>
                  {isLiveCapture && (
                    <button 
                      className="delete-page-btn" 
                      onClick={(e) => {
                        e.stopPropagation();
                        removePage(index);
                      }}
                      title={t('solutionCaptureDeletePage')}
                    >
                      <img src={crossIcon} alt="Delete" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        );
      }
      return <UserMessage key={msg.id} text={msg.text} initials={userInitials} />;
    }

    if (msg.type === 'chart') {
      return (
        <div key={msg.id} className="fade-in chat-image-block" onClick={() => handleChartClick(msg.text)}>
          <img
            src={arrowFullscreen}
            alt="Enlarge"
            className="fullscreen-icon"
            title={t("enlarge")}
          />
          <div
            className="chat-image-wrapper"
            dangerouslySetInnerHTML={{ __html: msg.text }}
          />
        </div>
      );
    }

    const isCompleted = completedMessageIds.has(msg.id);

    // If it's a questions list card, we render the questions card directly
    // and skip rendering the "Exercise sections" title BotMessage bubble.
    if (msg.type === 'questions_list') {
      return (
        <div key={msg.id} className="fade-in questions-bubble-card">
          <div className="practice-questions-card-container">
            <div className="questions-accordion-list">
              {exerciseQuestions.map((q, idx) => {
                const isSelected = selectedQuestionId === q.id;
                const letter = hebrewLetters[idx] || String(idx + 1);

                return (
                  <div
                    key={q.id}
                    className={`question-item-card step-accordion-item ${isSelected ? 'active' : ''} ${q.isCompleted ? 'completed' : ''} ${q.isInProcess && !q.isCompleted ? 'in-process' : ''}`}
                    onClick={() => selectQuestionFromBubble(q, letter)}
                  >
                    <div className="step-accordion-header">
                      <span className="step-title-text">
                        {t("section")} {letter}
                      </span>
                      <div className="question-status-pills">
                        {q.isCompleted ? (
                          <span className="status-pill completed-pill">{t("completed")}</span>
                        ) : (q.isInProcess ? (
                          <span className="status-pill in-process-pill">{t("inProgress")}</span>
                        ) : null)}
                      </div>
                    </div>
                    <div className="step-accordion-details">
                      <MathExpressionNew content={q.question} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    }

    // If it's a steps list card, we render the steps card directly
    // and skip rendering the title BotMessage bubble.
    if (msg.type === 'steps_list') {
      return (
        <div key={msg.id} className="fade-in steps-bubble-card">
          <div className="steps-card-container">
            <div className="steps-accordion-list">
              {msg.steps && msg.steps.map((step, idx) => (
                <div
                  key={idx}
                  className="step-accordion-item"
                  onClick={() => displayStepInline(idx, msg.steps!, msg.questionId!, msg.questionLetter!)}
                >
                  <div className="step-accordion-header">
                    <span className="step-number-tag">{idx + 1}</span>
                    <span className="step-title-text">
                      <MathExpressionNew content={step.title} />
                    </span>
                  </div>
                  <div className="step-accordion-details">
                    <MathExpressionNew content={step.content} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    // If it's options type with no text, we render the action buttons directly without a bot message bubble
    if (msg.type === 'options' && !msg.text) {
      return (
        <div key={msg.id} className={`exercise-practice-page__actions ${msg.optionsClassName || ''} fade-in`} style={{ marginTop: '12px' }}>
          {msg.options?.map((opt, i) => (
            <LessonActionBtn
              key={i}
              onClick={opt.action!}
              text={opt.label}
              color={opt.color || (i === 0 ? "black" : "dark")}
              variant={opt.variant || (i === 0 ? "contained" : "outlined")}
              icon={opt.icon}
              iconRight={opt.iconRight}
              disabled={isUploading}
            />
          ))}
        </div>
      );
    }

    if (msg.type === 'qr_code') {
      return (
        <div key={msg.id} className="solution-capture-qr-container fade-in">
          <div className="solution-capture-qr-card">
            <h6 className="qr-card-title">{t("solutionCaptureQRTitle")}</h6>
            <p className="qr-card-subtitle">{t("solutionCaptureQRSubtitle")}</p>
            
            <div className="qr-code-wrapper">
              {msg.isGenerating ? (
                <div className="qr-code-loader-overlay qr-code-generating-loader">
                  <img src={Loader} alt="Loader" className="qr-loader-spinner" />
                  <span>{t("solutionCaptureGeneratingQR")}</span>
                </div>
              ) : (
                <>
                  <QRCode value={msg.text} size={200} />
                  {isMobileActive && (
                    <div className="qr-code-loader-overlay">
                      <img src={Loader} alt="Loader" className="qr-loader-spinner" />
                      <span>{t("solutionCaptureMobileConnected")}</span>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="qr-card-actions">
              <LessonActionBtn
                text={t("solutionCaptureUploadFromPC")}
                onClick={startManualCapture}
                color="dark"
                variant="outlined"
                disabled={isUploading || msg.isGenerating}
              />
            </div>
          </div>
        </div>
      );
    }

    if (msg.type === 'success_banner') {
      return (
        <div key={msg.id} className="fade-in success-banner-chat-wrapper" style={{ marginTop: '12px', marginBottom: '12px' }}>
          <SuccessBanner
            points={msg.points}
            message={msg.bannerMessage}
            animate={true}
            variant={msg.variant}
          />
        </div>
      );
    }

    return (
      <div key={msg.id} className="bot-message-wrapper">
        <BotMessage
          text={msg.text}
          loadingText={msg.loadingText}
          isLoading={msg.type === 'loader_bubble'}
          onComplete={() => onMessageComplete(msg.id)}
          onNavigate={handleBotMessageNavigate}
        />

        {/* 1. Standard Exercise Card or List of Cards */}
        {isCompleted && msg.type === 'exercise' && (msg.exercise || msg.exercises) && (
          <div className="fade-in exercise-cards-list-wrapper">
            {loadingTitles ? (
              msg.exercises ? (
                Array.from({ length: exercise_list.length || 2 }).map((_, idx) => (
                  <div key={idx} className="exercise-practice-page__exercise-card exercise-card-skeleton">
                    <div className="exercise-icon skeleton-shimmer"></div>
                    <div className="exercise-info">
                      <div className="exercise-label skeleton-shimmer"></div>
                      <div className="exercise-title skeleton-shimmer"></div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="exercise-practice-page__exercise-card exercise-card-skeleton">
                  <div className="exercise-icon skeleton-shimmer"></div>
                  <div className="exercise-info">
                    <div className="exercise-label skeleton-shimmer"></div>
                    <div className="exercise-title skeleton-shimmer"></div>
                  </div>
                </div>
              )
            ) : msg.exercises ? (
              msg.exercises.map((ex: any) => {
                const historyItem = exerciseHistory.find((h: any) => h.exercise_id === ex.id);
                const title = exerciseTitles[ex.id] || ex.title;
                const isExCompleted = historyItem?.is_completed === true;
                const isExInProcess = historyItem?.is_completed === false;

                return (
                  <div
                    key={ex.id}
                    className={`exercise-practice-page__exercise-card ${isExCompleted ? 'completed' : ''} ${isExInProcess ? 'in-process' : ''}`}
                    onClick={() => navigate(ROUTERS_PATH.DISPLAYEXERCISE, { state: { ...location.state, exerciseId: ex.id, returnToExerciseList: true } })}
                  >
                    <div className="exercise-icon">
                      <img src={exerciseIcon} alt={t('exercisePracticeExerciseLabel')} />
                    </div>
                    <div className="exercise-info">
                      <div className="exercise-label">{t('exercisePracticeExerciseLabel')}</div>
                      <div className="exercise-title">{title}</div>
                    </div>
                    <div className="exercise-status-pills">
                      {isExCompleted ? (
                        <span className="status-pill completed-pill">{t("completed")}</span>
                      ) : (isExInProcess ? (
                        <span className="status-pill in-process-pill">{t("inProgress")}</span>
                      ) : null)}
                    </div>
                  </div>
                );
              })
            ) : msg.exercise ? (
              (() => {
                const historyItem = exerciseHistory.find((h: any) => h.exercise_id === msg.exercise?.id);
                const title = exerciseTitles[msg.exercise.id] || msg.exercise.title;
                const isExCompleted = historyItem?.is_completed === true;
                const isExInProcess = historyItem?.is_completed === false;

                return (
                  <div
                    className={`exercise-practice-page__exercise-card ${isExCompleted ? 'completed' : ''} ${isExInProcess ? 'in-process' : ''}`}
                    onClick={() => navigate(ROUTERS_PATH.DISPLAYEXERCISE, { state: { ...location.state, exerciseId: msg.exercise?.id, returnToExerciseList: true } })}
                  >
                    <div className="exercise-icon">
                      <img src={exerciseIcon} alt={t('exercisePracticeExerciseLabel')} />
                    </div>
                    <div className="exercise-info">
                      <div className="exercise-label">{t('exercisePracticeExerciseLabel')}</div>
                      <div className="exercise-title">{title}</div>
                    </div>
                    <div className="exercise-status-pills">
                      {isExCompleted ? (
                        <span className="status-pill completed-pill">{t("completed")}</span>
                      ) : (isExInProcess ? (
                        <span className="status-pill in-process-pill">{t("inProgress")}</span>
                      ) : null)}
                    </div>
                  </div>
                );
              })()
            ) : null}
          </div>
        )}

        {/* 4. Connection Error Fallback Bubble with Retry */}
        {msg.type === 'error_bubble' && (
          <div className="fade-in error-bubble-card">
            <div className="error-card-actions">
              <LessonActionBtn
                text={t("retry")}
                onClick={() => {
                  if (msg.errorContext === 'questions') {
                    loadQuestionsList(true);
                  } else if (msg.errorContext === 'qr_timeout') {
                    startCapture();
                  } else {
                    loadQuestionSteps(true);
                  }
                }}
                color="red"
                variant="contained"
                className="retry-btn"
              />
            </div>
          </div>
        )}

        {/* 5. Standard Options Chips */}
        {isCompleted && msg.options && (
          <div className={`exercise-practice-page__actions ${msg.optionsClassName || ''} fade-in`}>
            {msg.options.map((opt, i) => (
              <LessonActionBtn
                key={i}
                onClick={opt.action!}
                text={opt.label}
                color={opt.color || (i === 0 ? "black" : "dark")}
                variant={opt.variant || (i === 0 ? "contained" : "outlined")}
                icon={opt.icon}
                iconRight={opt.iconRight}
                disabled={isUploading}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  const isFemale = userInfo?.gender === "female" || user_attributes?.gender === 2 || user_attributes?.gender === "female";
  const genderSuffix = isFemale ? "_female" : "_male";
  const solutionCropTitle = t("solutionCaptureTitle");
  const solutionCropDescription = t(`solutionCaptureCropInstruction${genderSuffix}`);

  return (
    <>
      {/* Crop Page View */}
      {fileToCrop && cropImageUrl && (
        <div className="full-template capture-exercise-page capture-exercise-page-mobile" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <div>
            <ChatHeader chatHeading={solutionCropTitle} onBackClick={() => setFileToCrop(null)} />
          </div>
          <div className="capture-exercise-body">
            <div className="capture-exercise-body-inner">
              <div className="capture-exercise-body-text">
                <p>{solutionCropDescription}</p>
              </div>
              <div className="capture-exercise-image">
                <div className="cropper-container-wrapper">
                  <div className="cropper-container">
                    {isUploading && (
                      <div className="upload-overlay">
                        <img src={Loader} alt="Uploading..." />
                      </div>
                    )}
                    <ReactCrop
                      crop={crop}
                      onChange={(c) => setCrop(c)}
                      onComplete={(c) => setCompletedCrop(c)}
                      aspect={undefined}
                      minWidth={100}
                      minHeight={20}
                      ruleOfThirds={true}
                      keepSelection={true}
                    >
                      <img
                        ref={imgRef}
                        alt="Crop solution"
                        src={cropImageUrl}
                        className="cropper-image"
                        onLoad={onImageLoad}
                      />
                    </ReactCrop>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="capture-exercise-buttons">
            <Button text={t("cancel")} color="blue" className="border-button" onClick={() => setFileToCrop(null)} disabled={isUploading} />
            <Button text={t("selectImage")} color="blue" onClick={handleConfirmCrop} disabled={isUploading} loader={isUploading} is_white_loader={true} />
          </div>
        </div>
      )}

      {/* Main Chat View */}
      <div 
        className="exercise-practice-page" 
        ref={pageRef}
        style={{ display: (fileToCrop && cropImageUrl) ? 'none' : 'flex' }}
      >
        <main className="exercise-practice-page__chat" ref={chatContainerRef}>
          <div className="exercise-practice-page__scroll-wrapper" ref={scrollContentRef}>
            {messages.map(renderMessage)}

            {isTyping && <BotMessage isLoading playAudio={false} />}

            <div className="exercise-practice-page__bottom-spacer" />
          </div>
        </main>

        <ChatInputTool
          onSendMessage={addUserMessage}
          onMicClick={() => { }}
          onAttachClick={() => { }}
          onStudyModeClick={openModesModal}
          placeholder={t('exercisePracticePlaceholder')}
        />

        {zoomImageSrc && <ZoomChartModal imageSrc={zoomImageSrc} onClose={() => setZoomImageSrc(null)} />}

        {/* Sticky Tab */}
        {location.state?.selectedExercise && (
          <div className="orange-calculator-tab" onClick={() => setIsDrawerOpen(true)}>
            <img src={Calculator} alt="Calculator" className="calculator-icon" />
            {showTooltip && (
              <div className="calculator-tooltip">
                <div className="tooltip-arrow" />
                {t("viewOriginalExercise")}
              </div>
            )}
          </div>
        )}

        {/* Custom Sliding Drawer */}
        <div className={`exercise-drawer-overlay ${isDrawerOpen ? 'open' : ''}`} onClick={() => setIsDrawerOpen(false)}>
          <div className="exercise-drawer-container" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <div className="drawer-title-container">
                <img src={calculatorIcon} alt="Calculator" className="calculator-header-icon" />
                <h3 className="drawer-title">{t("exercise")}</h3>
              </div>
              <IconButton onClick={() => setIsDrawerOpen(false)} className="close-btn">
                <img src={arrowLeft} alt="Close" />
              </IconButton>
            </div>
            <div className="drawer-body">
              {capturedImageMsg && (capturedImageMsg.imageUrls || capturedImageMsg.imageUrl) ? (
                <div className="drawer-captured-images-gallery">
                  {(capturedImageMsg.imageUrls || [capturedImageMsg.imageUrl]).map((url, idx) => url && (
                    <div key={idx} className="captured-image-preview" onClick={() => setZoomImageSrc(url)}>
                      <img src={url} alt={`Exercise - Page ${idx + 1}`} className="captured-image" />
                      <div className="drawer-page-badge">{idx + 1}</div>
                    </div>
                  ))}
                </div>
              ) : exerciseContent ? (
                <div className="exercise-text-content">
                  <MathExpressionNew content={exerciseContent} />
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* Solution Analysis Bottom Sheet Modal */}
        <SolutionAnalysisModal
          open={isAnalysisModalOpen}
          onClose={() => setIsAnalysisModalOpen(false)}
          data={solutionAnalysisData}
        />
      </div>
    </>
  );
};

export default ExercisePractice;
